/**
 * `GET`, `PATCH` and `DELETE /api/v1/report-schedules/{id}` — Task U19.
 *
 * ── Why this is a box with an id in it and not a table ─────────────────────
 *
 * Because **there is no `GET /report-schedules`**. `src/modules/schedules/routes.ts`
 * mounts `POST` on the collection and three operations on `/{id}`, and nothing that
 * enumerates: re-read there, not inferred from `openapi.yaml`. So a browser cannot
 * answer *"what standing exports is this company running?"*, and a panel that drew a
 * table would have to invent its rows. It works on **one** id — the one the create
 * form just produced, or one somebody wrote down — and `AbsentEndpoints` still names
 * the missing list as the reason.
 *
 * ── `DELETE` deletes. It does not "end". ───────────────────────────────────
 *
 * This is the finding this component exists to not paper over. The unit's brief asked
 * for a control that *ends* a schedule and never removes the record; the server does
 * the opposite, deliberately and with its reasoning written down:
 * `schedules-repo.ts`'s `deleteSchedule` is `DELETE FROM wp_report_schedules …
 * RETURNING`, and its header says in as many words that *"delete and disable are not
 * the same act, and are not collapsed"* — `PATCH { enabled: false }` **pauses** (the
 * row, the recipients and `next_run_at` all stay, and it can be switched back on) and
 * `DELETE` **retires** (the row is gone, and there is nothing to switch back on).
 * Migration 025's `wp_reports_schedule_fk … ON DELETE SET NULL` is the schema saying
 * the same thing: a schedule may be removed and the reports it made outlive it.
 *
 * So the screen does **not** relabel the delete as an ending, and does not quietly
 * send `PATCH { enabled: false }` from a control captioned "end". It offers both acts
 * under their own names, and the destructive one carries the sentence that says the
 * row is deleted, that nothing brings it back, and that the audit row is what is left.
 * Wiring a delete under a softer word would be this client asserting a lifecycle the
 * database does not have.
 *
 * ── The re-read after a delete is a confirmation, not an error ─────────────
 *
 * A `204` carries no body, so "it is gone" would otherwise be this component's own
 * claim. It re-reads the id instead and the `404` is the server confirming it. That
 * one expected `404` renders as the outcome note rather than as a refusal — every
 * other failure, the `404` included when nothing was deleted, still renders through
 * `ApiErrorNote` with the server's own sentence verbatim.
 *
 * Two guards keep that note from becoming a claim of the component's own, because
 * both halves of it — "the server answered `204`" and "the re-read found nothing" —
 * are statements about things that may not have happened:
 *
 *  1. **`deletedId` is set only on a success.** `useApiMutation.submit` *resolves*
 *     rather than rejects (its own header says so), so a `.then()` that marked the id
 *     deleted would mark it deleted on a refusal too — hiding the card, suppressing
 *     the read error, and printing "the server answered 204" beside the refusal note
 *     saying it did not. The success is read from `remove.status === 'ok'` in an
 *     effect, the way `AlertsScreen` reads its acknowledgement; `remove.data` is no
 *     use here because `TResult = undefined` leaves it with nothing to key on.
 *  2. **The confirmation sentence needs the `404`.** A `500`, a transport failure, or
 *     a `200` still carrying the record means the re-read confirmed nothing, and the
 *     note says *that* rather than reporting a confirmation it does not have.
 *
 * A **refused** `DELETE` therefore issues no re-read of its own: there is no deletion
 * to confirm, and the panel goes on showing the last card the server did answer with
 * the refusal beside it. That is only honest while the operator has a way to ask for
 * a fresh one, and **Read the schedule** is it — `select()` bumps `readToken`, so
 * pressing it on the id already selected is a real `GET`. Without that bump it was
 * not: `onSelect` is `setScheduleId`, so the press was a same-value `setState` that
 * issued nothing while `select()`'s own `resetRemove()` wiped the server's refusal off
 * the screen — the panel dismissing a denial and going back to a card it had not
 * re-checked.
 *
 * ── The precondition comes from the read, never from the body ──────────────
 *
 * `PATCH` requires `If-Match` and refuses a request without one with a `400` naming
 * the header. The token is `read.etag` and **only** `read.etag` — the `ETag` the
 * response actually carried. The comment on `const etag = read.etag` below is the
 * reasoning: on this surface every write re-reads, so preferring the write's own
 * `toggle.etag` would pin a token that goes on shadowing every later read. It is
 * never reconstructed from `updated_at` in the body either: WP-L5 settled that, and a
 * token a client rebuilt is a token that is wrong by three digits of microseconds. A
 * read that carried none leaves **no control at all** plus a sentence — an absence,
 * not a greyed button, because the problem is not the operator's permission.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import { reportSchedulePath } from '../../api/paths';
import type { ReportSchedule, ReportSchedulePatchRequest } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { useApiMutation } from '../../api/useApiMutation';
import { useSession } from '../../auth/SessionProvider';
import { formatInstant } from '../../i18n/datetime';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

export interface ScheduleManagerProps {
  /** The schedule this panel is looking at, or `null` for "none chosen yet". */
  readonly scheduleId: string | null;
  /** Raised when the operator picks a different id. The parent owns the choice. */
  readonly onSelect: (scheduleId: string | null) => void;
}

/** `PATCH /report-schedules/{id}` and `DELETE` share the path; neither is bound at mount. */
const UNBOUND = reportSchedulePath('unset');

export function ScheduleManager({ scheduleId, onSelect }: ScheduleManagerProps): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const [typedId, setTypedId] = useState('');
  const [readToken, setReadToken] = useState(0);
  // The id this panel has deleted, so the `404` that follows can be told apart from
  // the `404` of an id that was never there. Cleared when a different id is chosen.
  const [deletedId, setDeletedId] = useState<string | null>(null);
  // The id the last `DELETE` was *sent* for, which is not the same fact. It is what
  // the success effect below marks deleted, so a `204` can only ever be attributed to
  // the id it was actually sent for and never to whichever id is on screen when it
  // lands.
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const path = scheduleId === null ? null : reportSchedulePath(scheduleId);
  const read = useApiQuery<{ data: ReportSchedule }>(path, readToken);
  const toggle = useApiMutation<ReportSchedulePatchRequest, { data: ReportSchedule }>(
    UNBOUND,
    'PATCH',
  );
  // `TBody = undefined`: `DELETE /report-schedules/{id}` takes no request body, and
  // sending `{}` would be a body the route's `.strict()` schema never asked for.
  const remove = useApiMutation<undefined, undefined>(UNBOUND, 'DELETE');

  const zone = user?.timezone ?? 'Asia/Ho_Chi_Minh';
  const schedule = read.data?.data ?? null;
  /**
   * The precondition, and **only** the read's.
   *
   * `IncidentsScreen` prefers the write's own response ETag so a second `PATCH` in a
   * row needs no re-read; that is right there because the write is the last thing
   * that happens. Here every write re-reads, so a write's token would be the *older*
   * of the two the moment the re-read landed — and a sticky `toggle.etag` would go on
   * shadowing every later read for the life of the panel. The freshest token on this
   * surface is always the one the last `GET` carried.
   */
  const etag = read.etag;
  const justDeleted = deletedId !== null && deletedId === scheduleId;

  // `reset` is a `useCallback(…, [])` inside `useApiMutation`, so naming the two here
  // gives `select` and the effect below a stable dependency — the mutation objects
  // themselves are fresh literals on every render.
  const resetToggle = toggle.reset;
  const resetRemove = remove.reset;

  const select = useCallback(
    (next: string | null) => {
      setDeletedId(null);
      setDeleteTarget(null);
      resetToggle();
      resetRemove();
      // **The read control has to read.** `onSelect` is `setScheduleId`
      // (`ReportsScreen.tsx`), so choosing the id that is already chosen is a
      // same-value `setState`: `path` does not change, `useApiQuery`'s effect deps do
      // not change, and the press issued no request at all — while `resetRemove()`
      // above had already taken the server's refusal off the screen. A control that
      // clears a denial and asks nothing is the defect this panel exists to remove.
      // The token is what makes the same id a fresh `GET`, and it is what makes true
      // the two sentences that send an operator here: `deleted.unconfirmed` ("press
      // Read again on the same id") and `noEtag` ("read it again to obtain an ETag").
      //
      // A *different* id bumps this in the same commit as the `scheduleId` change, so
      // both reach `useApiQuery`'s deps together and still produce exactly one read.
      setReadToken((value) => value + 1);
      onSelect(next);
    },
    [onSelect, resetRemove, resetToggle],
  );

  /**
   * The same clearing, driven by the id changing rather than by the operator asking.
   *
   * `ReportsScreen.onScheduleCreated` sets `scheduleId` **directly** — a schedule the
   * create form just made opens here without passing through `select()` — so a reset
   * that lived only in `select()` would leave a refused `PATCH` or `DELETE` from
   * schedule A on screen underneath freshly created schedule B, a refusal attached to
   * a record it was never about. Doing it here covers both entry points. `select()`
   * keeps its own copy because re-reading the *same* id changes nothing for this
   * effect to fire on.
   */
  useEffect(() => {
    setDeletedId(null);
    setDeleteTarget(null);
    resetToggle();
    resetRemove();
  }, [scheduleId, resetRemove, resetToggle]);

  const flipEnabled = useCallback(() => {
    if (scheduleId === null || schedule === null || etag === null) return;
    void toggle
      // Quoted: `If-Match` is an entity-tag header, `api/client.ts` unquotes on the
      // way in, and `ifMatchToken` refuses a value that is not the shape it minted.
      .submit({ enabled: !schedule.enabled }, reportSchedulePath(scheduleId), {
        'If-Match': `"${etag}"`,
      })
      .then(() => {
        // Re-read regardless of the outcome. A success moved `enabled`; a refusal
        // means what is on screen is older than the record, and both are answered by
        // asking the server rather than by patching the local copy.
        setReadToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal stays on screen. */
      });
  }, [etag, schedule, scheduleId, toggle]);

  const end = useCallback(() => {
    if (scheduleId === null) return;
    // Recorded before the write, not after it: what the success below is allowed to
    // mark deleted is the id the `DELETE` went to.
    setDeleteTarget(scheduleId);
    void remove.submit(undefined, reportSchedulePath(scheduleId)).catch(() => {
      /* `submit` resolves rather than rejects; the refusal stays on screen. */
    });
  }, [remove, scheduleId]);

  /**
   * A `204`, and nothing else, is what marks the id deleted.
   *
   * This cannot be the `.then()` it used to be: `submit` resolves on a refusal too, so
   * a `.then()` set `deletedId` on every settled `DELETE` — which hid the card,
   * suppressed the read error and printed "the server answered 204 and the page
   * re-read the id to confirm" next to the refusal note saying the opposite.
   * `remove.data` cannot stand in for the status the way `AlertsScreen`'s
   * `acknowledged` does, because a body-less `DELETE` is instantiated at
   * `TResult = undefined` and its `data` is `undefined` after a success as well as
   * before one. The re-read is bumped from here for the same reason: it exists to
   * confirm a deletion, and a refusal has no deletion to confirm. What a refusal
   * leaves behind is the last card the server actually answered plus the refusal
   * itself, and the way back to the record is the **Read** control — which re-reads
   * the same id because `select()` bumps this same token.
   */
  useEffect(() => {
    if (remove.status !== 'ok' || deleteTarget === null) return;
    setDeletedId(deleteTarget);
    setReadToken((value) => value + 1);
  }, [deleteTarget, remove.status]);

  const pending = toggle.status === 'pending';
  const deleting = remove.status === 'pending';

  return (
    <section
      className="wp-section"
      aria-labelledby="k8-sched-manage-heading"
      data-testid="k8-sched-manage"
    >
      <h2 className="wp-section__title" id="k8-sched-manage-heading">
        {t('k8.sched.manage.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="k8-sched-manage-intro">
        {t('k8.sched.manage.intro')}
      </p>

      <form
        className="wp-filters wp-filters--bar"
        aria-labelledby="k8-sched-manage-heading"
        onSubmit={(event) => {
          event.preventDefault();
          const wanted = typedId.trim();
          if (wanted === '') return;
          select(wanted);
        }}
      >
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="k8-sched-manage-id">
            {t('k8.sched.manage.idLabel')}
          </label>
          <input
            className="wp-field__input"
            id="k8-sched-manage-id"
            data-testid="k8-sched-manage-id"
            type="text"
            autoComplete="off"
            value={typedId}
            onChange={(event) => {
              setTypedId(event.target.value);
            }}
          />
        </div>
        {/* The honest disabled: it says "you have not typed an id", which is a
            statement about the operator's own draft and not about permission. */}
        <button
          className="wp-button"
          type="submit"
          data-testid="k8-sched-manage-read"
          disabled={typedId.trim() === ''}
        >
          {read.status === 'loading' && path !== null
            ? t('k8.sched.manage.reading')
            : t('k8.sched.manage.read')}
        </button>
      </form>

      {justDeleted ? (
        <StatusNote
          severity="info"
          title={t('k8.sched.manage.deleted.title', { id: deletedId })}
          testId="k8-sched-manage-deleted"
        >
          {/* Three outcomes, not two. The confirmation sentence says the re-read
              found nothing, so it is spoken only when the re-read actually answered
              `404`: a `500`, a transport failure, or a `200` still carrying the
              record all mean the deletion is unconfirmed, and the third sentence says
              so rather than claiming a confirmation the panel never got. */}
          <p>
            {read.status === 'loading'
              ? t('k8.sched.manage.deleted.pending')
              : read.error?.code === 'WORKFORCE_NOT_FOUND'
                ? t('k8.sched.manage.deleted.body')
                : t('k8.sched.manage.deleted.unconfirmed')}
          </p>
        </StatusNote>
      ) : null}

      {schedule === null || justDeleted ? null : (
        <article className="wp-card" data-testid={`k8-sched-card-${schedule.id}`}>
          <h3 className="wp-panel__title" tabIndex={-1}>
            {schedule.name}
          </h3>
          <p data-testid={`k8-sched-state-${schedule.id}`}>
            {schedule.enabled
              ? t('k8.sched.manage.state.enabled')
              : t('k8.sched.manage.state.paused')}
          </p>

          <dl className="wp-figures">
            <div className="wp-figures__item">
              <dt>{t('k8.sched.manage.nextRun')}</dt>
              <dd className="wp-small" data-testid={`k8-sched-next-${schedule.id}`}>
                {formatInstant(schedule.next_run_at, locale, zone)}
              </dd>
            </div>
            <div className="wp-figures__item">
              <dt>{t('k8.sched.manage.lastRun')}</dt>
              <dd className="wp-small" data-testid={`k8-sched-last-${schedule.id}`}>
                {/* `null` here is "has never run" and is said in words. A blank cell
                    beside a schedule reads as a run whose time nobody recorded. */}
                {schedule.last_run_at === null || schedule.last_run_at === undefined
                  ? t('k8.sched.manage.neverRun')
                  : formatInstant(schedule.last_run_at, locale, zone)}
              </dd>
            </div>
            <div className="wp-figures__item">
              <dt>{t('k8.sched.manage.lastStatus')}</dt>
              <dd className="wp-small" data-testid={`k8-sched-outcome-${schedule.id}`}>
                {/* `last_status === null` is **two facts**, told apart by `last_run_at`:
                    with no run it has never run, and with a run it is in flight —
                    the outcome columns are cleared at enqueue so a retry does not
                    still show last week's success. Collapsing the two would present a
                    running occurrence as a schedule that had never fired. */}
                {schedule.last_status === null || schedule.last_status === undefined
                  ? schedule.last_run_at === null || schedule.last_run_at === undefined
                    ? t('k8.sched.manage.neverRun')
                    : t('k8.sched.manage.inFlight')
                  : t(`k8.sched.manage.status.${schedule.last_status}` as MessageKey)}
              </dd>
            </div>
            <div className="wp-figures__item">
              <dt>{t('k8.sched.manage.failures')}</dt>
              <dd data-testid={`k8-sched-failures-${schedule.id}`}>
                {schedule.consecutive_failures ?? 0}
              </dd>
            </div>
            <div className="wp-figures__item">
              <dt>{t('k8.sched.manage.recipients')}</dt>
              <dd className="wp-small" data-testid={`k8-sched-recipients-${schedule.id}`}>
                {(schedule.recipients ?? []).join(', ')}
              </dd>
            </div>
          </dl>

          {etag === null ? (
            // No control at all rather than a greyed one: the missing precondition is
            // not a permission problem, and a `PATCH` without `If-Match` is refused by
            // the server anyway.
            <StatusNote
              severity="info"
              title={t('k8.sched.manage.title')}
              testId={`k8-sched-noetag-${schedule.id}`}
            >
              <p>{t('k8.sched.manage.noEtag')}</p>
            </StatusNote>
          ) : (
            <>
              <button
                className="wp-button"
                type="button"
                data-testid={`k8-sched-toggle-${schedule.id}`}
                onClick={flipEnabled}
                disabled={pending}
              >
                {pending
                  ? t('k8.sched.manage.toggling')
                  : schedule.enabled
                    ? t('k8.sched.manage.pause')
                    : t('k8.sched.manage.resume')}
              </button>
              <p className="wp-small wp-muted" data-testid={`k8-sched-pause-note-${schedule.id}`}>
                {t('k8.sched.manage.pauseNote')}
              </p>
            </>
          )}

          <button
            className="wp-button wp-button--danger"
            type="button"
            data-testid={`k8-sched-delete-${schedule.id}`}
            onClick={end}
            disabled={deleting}
          >
            {deleting ? t('k8.sched.manage.deleting') : t('k8.sched.manage.delete')}
          </button>
          <p className="wp-small wp-muted" data-testid={`k8-sched-delete-note-${schedule.id}`}>
            {t('k8.sched.manage.deleteNote')}
          </p>
        </article>
      )}

      {/* Every refusal renders the server's own sentence. The one exception is the
          `404` that follows this panel's own delete, which is the confirmation above
          rather than a refusal — and it is scoped to that exact id, so a `404` for an
          id nobody deleted still arrives here. */}
      {read.status === 'error' && read.error !== null && !justDeleted ? (
        <ApiErrorNote
          error={read.error}
          onRetry={read.reload}
          subject={t('k8.sched.manage.subject')}
          testId="k8-sched-manage-error"
        />
      ) : null}

      {toggle.status === 'error' && toggle.error !== null ? (
        <ApiErrorNote
          error={toggle.error}
          subject={t('k8.sched.manage.subject')}
          testId="k8-sched-toggle-error"
        />
      ) : null}

      {remove.status === 'error' && remove.error !== null ? (
        <ApiErrorNote
          error={remove.error}
          subject={t('k8.sched.manage.subject')}
          testId="k8-sched-delete-error"
        />
      ) : null}
    </section>
  );
}
